import { Box, Button, Tab, Tabs } from "@mui/material";
import { PlusIcon } from "app/icons";
import { addOrderModalActions } from "app/containers/CommonModal/redux/slice";
import { AddOrderModalContext } from "app/containers/CommonModal/types";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

import {
  CUSTOMER_DETAIL_TAB_ACTION_LABELS,
  CUSTOMER_DETAIL_TAB_LABELS,
  CustomerActivity,
  CustomerDetailTab,
  CustomerDocument,
  CustomerPriceQuote,
  CustomerWorkOrder,
  Financial
} from "../../../types";
import AddPriceQuoteModal from "../PriceQuotesTable/AddPriceQuoteModal";
import PriceQuotesTable from "../PriceQuotesTable";
import AddWorkOrderModal from "../WorkOrder/AddWorkOrderModal";
import WorkOrder from "../WorkOrder";
import FinancialTable from "../Financial"
import Document from "../Document"
import AddDocumentModal from "../Document/AddDocumentModal";
import TimeLine from "../TimeLine";
import { customersActions } from "../../../redux/slice";
import { useStyles } from "./styles";

type Props = {
  customerName: string;
  priceQuotes: CustomerPriceQuote[];
  workOrders: CustomerWorkOrder[];
  FinancialRows: Financial[]
  documentRows: CustomerDocument[]
  timelineRows: CustomerActivity[]
};

const TAB_MODAL_CONTEXT: Partial<
  Record<CustomerDetailTab, AddOrderModalContext>
> = {
  [CustomerDetailTab.WORK_INSTRUCTIONS]: AddOrderModalContext.WORK_ORDER,
};

const CustomerDetailTabs = ({
  customerName,
  priceQuotes,
  workOrders,
  FinancialRows,
  documentRows,
  timelineRows
}: Props) => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();
  const [activeTab, setActiveTab] = useState(CustomerDetailTab.PRICE_QUOTE);
  const [isPriceQuoteModalOpen, setIsPriceQuoteModalOpen] = useState(false);
  const isHebrew = (i18n.language || "hw").startsWith("hw");

  const tabsToRender = useMemo(() => {
    const tabs = Object.values(CustomerDetailTab);
    return isHebrew ? [...tabs].reverse() : tabs;
  }, [isHebrew]);

  const actionLabel = t(CUSTOMER_DETAIL_TAB_ACTION_LABELS[activeTab]);
  const modalContext = TAB_MODAL_CONTEXT[activeTab];

  const handleAddClick = () => {
    if (activeTab === CustomerDetailTab.FILES) {
      dispatch(customersActions.openAddDocumentModal());
      return;
    }

    if (activeTab === CustomerDetailTab.PRICE_QUOTE) {
      setIsPriceQuoteModalOpen(true);
      return;
    }

    if (!modalContext) {
      return;
    }

    dispatch(
      addOrderModalActions.openAddOrderModal({
        context: modalContext,
        customerName,
      })
    );
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case CustomerDetailTab.WORK_INSTRUCTIONS:
        return <WorkOrder rows={workOrders} />;
      case CustomerDetailTab.PRICE_QUOTE:
        return <PriceQuotesTable rows={priceQuotes} />;
      case CustomerDetailTab.FINANCIAL:
        return <FinancialTable rows={FinancialRows}  />
      case CustomerDetailTab.FILES:
        return <Document rows={documentRows} />
      case CustomerDetailTab.TIMELINE:
        return <TimeLine rows={timelineRows} />
      default:
        return <PriceQuotesTable rows={priceQuotes} />;
    }
  };

  return (
    <Box className={styles.root}>
      <Box className={styles.tabBarRow}>
        {activeTab !== CustomerDetailTab.TIMELINE && (
          <Button
            variant="contained"
            className={styles.actionButton}
            startIcon={<PlusIcon />}
            onClick={handleAddClick}
          >
            {actionLabel}
          </Button>
        )}

        <Box className={styles.tabsWrapper}>
          <Tabs
            className={styles.tabs}
            value={activeTab}
            onChange={(_, nextValue) =>
              setActiveTab(nextValue as CustomerDetailTab)
            }
            variant="scrollable"
            scrollButtons={false}
          >
            {tabsToRender.map((tab) => (
              <Tab
                key={tab}
                label={t(CUSTOMER_DETAIL_TAB_LABELS[tab])}
                value={tab}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {renderTabContent()}
      <AddPriceQuoteModal
        open={isPriceQuoteModalOpen}
        onClose={() => setIsPriceQuoteModalOpen(false)}
      />
      <AddWorkOrderModal />
      <AddDocumentModal />
    </Box>
  );
};

export default CustomerDetailTabs;
